@use '../../resources/styles/tokens' as *;
@import '../../resources/styles/common-mag.scss';
@import '../../resources/styles/a11y-mag.scss';

$inputHeight: 48px;
$inputIconOffset: 14px;

$placeholderColour: $colour-grey-300;
$focusedPlaceholderColour: $colour-grey-300;

.container {
  position: relative;
  display: inline-block;

  &.short {
    width: 300px;
  }

  &.long {
    width: 624px;
  }

  &.fullWidth {
    width: 100%;
  }

  &:not(.fullWidth) {
    margin-right: $spacing-6x;

    #{$rtl} {
      margin-right: 0;
      margin-left: $spacing-6x;
    }
  }

  &.bottomMargin {
    margin-bottom: 20px;
  }
}

.title-block,
.inputContainer {
  width: 100%;
  display: inline-flex;
  position: relative;
}

.inputContainer input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 500px $colour-white inset;
}

.title-block {
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-2x;

  .optional {
    color: $colour-grey-700;
    font-size: 0.875rem;
    padding-top: $spacing-half;
  }

  > div {
    margin-right: 5px;

    #{$rtl} {
      margin-right: 0;
      margin-left: 5px;
    }
  }
}

.title {
  display: inline-flex;
  align-items: center;
  gap: $spacing-1x;

  > div {
    margin-right: 5px;

    #{$rtl} {
      margin-right: 0;
      margin-left: 5px;
    }
  }
}

.input {
  width: 100%;
  min-height: $inputHeight;
  flex: 1 1 auto;
  padding-left: 15px;
  padding-right: 10px;

  &.visibility {
    padding-right: 30%;
  }

  &.search {
    min-width: 295px;
    padding-right: 44px;
  }

  &.type-icons {
    padding-left: 55px;
  }

  #{$rtl} {
    padding-left: 10px;
    padding-right: 15px;

    &.visibility {
      padding-left: 30%;
    }

    &.type-icons {
      padding-right: 55px;
    }

    &.search {
      padding-left: 94px;
    }
  }

  line-height: 24px;
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 6px;
  outline: none;
  background-size: 20px;
  /*would be 24 px but icons should have a 2px margin*/
  background-repeat: no-repeat;

  background-position: 20px 50%;

  #{$rtl} {
    background-position: calc(100% - 20px) 50%;
  }

  border: 2px solid $colour-grey-300;

  &::-ms-clear {
    display: none;
    /* disables x button on IE */
  }

  &.light {
    background-color: $colour-white;
    color: $colour-text-primary;
  }

  &::placeholder {
    font-weight: 500;
  }

  & + svg {
    position: absolute;
    left: $inputIconOffset;
    top: 50%;
    transform: translate(0, -50%);
    pointer-events: none;

    #{$rtl} {
      left: auto;
      right: $inputIconOffset;
    }
  }

  & + svg path {
    fill: $colour-grey-300;
  }

  &.prompt {
    border: none;
    border-bottom: 2px dotted $colour-brand-primary-dark;
    border-radius: 0;
    padding: $spacing-6x 0 0 0;
    margin: 0 5px 22px;
    display: inline-block;
  }
}

//on ios safari, sometimes last input in a list are showing as hovered on load..
//since you (usually) can't hover on mobile anyway, guess at some mobile sizes; 750px is iphone 8 res
@media screen and (min-width: 751px) {
  .input.empty:not(:focus):not(:disabled):hover.light {
    background-color: $colour-white;
    border-color: $colour-brand-primary-dark;

    & + svg path {
      fill: $colour-brand-primary-dark;
    }

    & ~ .inner-icon {
      border-color: inherit;
    }
  }
}

.input:disabled {
  background-color: $colour-grey-100 !important;
  border-color: $colour-grey-300 !important;
  color: $colour-grey-700 !important;

  & ~ .inner-icon {
    border-color: $colour-grey-300;
  }

  & + svg path {
    fill: $colour-grey-700;
  }
}

.input:focus.light {
  background-color: $colour-white;
  color: $colour-text-primary;
  border-color: $colour-brand-primary-dark;

  & + svg path {
    fill: $colour-brand-primary-dark;
  }

  & ~ .inner-icon {
    border-color: $colour-brand-primary-dark;
  }
}

/* "prefilled" element -i.e. a value filled in initially, but not actively being edited.
   after editing, it is no longer prefilled (unless it's returned to its original value) */
.input.prefilled:not(.empty):not(.invalid).light {
  background-color: $colour-white;
  color: $colour-text-primary;
  border-color: $colour-grey-300;

  & ~ .inner-icon {
    border-color: $colour-grey-300;
  }
}

.input.prefilled:not(.empty):not(.always-show-invalid):hover.light {
  border-color: $colour-grey-300;
  color: $colour-text-primary;

  & ~ .inner-icon {
    border-color: $colour-grey-300;
  }
}

.invalid,
.valid {
  &.type-icons {
    padding-right: 50px;

    #{rtl} {
      padding-left: 50px;
    }
  }

  &.light {
    color: $colour-text-primary;
  }
}

.input.invalid {
  border-color: $colour-error-primary;

  &:hover {
    border-color: $colour-error-dark;

    & ~ .inner-icon {
      border-color: $colour-error-dark;
    }
  }

  & ~ .inner-icon {
    border-color: $colour-error-primary;
  }

  & + svg path,
  &:hover svg path {
    fill: $colour-brand-primary-dark;
  }
}

.input.valid {
  border-color: $colour-success-primary;

  &:hover {
    border-color: $colour-success-dark;

    & ~ .inner-icon {
      border-color: $colour-success-dark;
    }
  }

  & ~ .inner-icon {
    border-color: $colour-success-primary;
  }

  & + svg path,
  &:hover svg path {
    fill: $colour-brand-primary-dark;
  }
}

.input.always-show-invalid:focus {
  border-color: $colour-error-primary;

  & ~ .inner-icon {
    border-color: $colour-error-primary;
  }
}

.sadIcon,
.happyIcon,
.inner-icon {
  position: absolute;
  pointer-events: none;
  width: 24px;
  height: 24px;
  right: 20px;
  align-self: center;

  &.light {
    background-color: $colour-white;
    border-color: $colour-grey-300;
  }

  & + img {
    padding-right: 50px;
  }

  &:hover {
    cursor: pointer;
  }

  #{$rtl} {
    right: initial;
    left: 20px;
  }
}

.inner-button:not(.clear-icon) {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  border-style: solid;
  border-width: 2px;

  #{$rtl} {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
    right: initial;
    left: 20px;
  }
}

.input::-webkit-input-placeholder {
  color: $placeholderColour;
}

.input.focused::-webkit-input-placeholder {
  color: $focusedPlaceholderColour;
}

.input::-moz-placeholder {
  color: $placeholderColour;
}

.input.focused::-moz-placeholder {
  color: $focusedPlaceholderColour;
}

.input:-moz-placeholder {
  color: $placeholderColour;
}

.input.focused:-moz-placeholder {
  color: $focusedPlaceholderColour;
}

.input:-ms-input-placeholder {
  color: $placeholderColour;
}

.input.focused:-ms-input-placeholder {
  color: $focusedPlaceholderColour;
}

.input[data-type='text'],
.input[data-type='number'],
.input[data-type='number-increment'],
.multiLine {
  padding-left: 10px;

  #{$rtl} {
    padding-left: 0;
    padding-right: 15px;
  }
}

.multiLine {
  /* stylelint-disable-line block-no-redundant-nesting */
  #{$rtl} {
    text-align: right;
  }
}

.tooltipPositioner {
  position: absolute;
  top: $inputHeight - 6px;
  right: 85px;

  #{$rtl} {
    right: initial;
    left: 85px;
  }
}

.bottom-text {
  min-height: 30px;
}

.double-height {
  min-height: 53px;
}

.validation-text {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 5px 0;
  font-weight: 500;

  & > svg {
    min-height: 20px;
    min-width: 20px;
    margin-left: 10px;

    #{$rtl} {
      margin-left: 0;
      margin-right: 10px;
    }
  }
}

.counter {
  color: $colour-grey-300;
  height: 100%;
  padding: 5px 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  line-height: 24px;
}

.inner-icon {
  pointer-events: all;
  display: flex;
  min-width: fit-content;
  width: 90px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-left: 2px $colour-grey-300 solid;
  padding: 0 10px;
  right: 0;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &.clear-icon {
    height: 30px;
    width: 50px;
    border-left: none;
    padding: 0;
    right: $spacing-half;
  }

  & > svg {
    min-height: 24px;
    min-width: 24px;
  }

  #{$rtl} {
    left: 0;
    border-left: 0;
    border-right: 2px $colour-grey-300 solid;
  }
}

.inner-icon-text {
  min-width: fit-content;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  padding-left: 5px;

  &.light {
    color: $colour-black;
  }

  #{$rtl} {
    padding-right: 5px;
    padding-left: 0;
  }
}
